/**
 * Rekomendowana stawka VAT (W9) — odczyt pola `vatSuggestion` z API.
 *
 * Reguła (macierz VAT klienta, 25.09.2026) żyje w API
 * (`apps/api/src/user-offers/vat-suggestion.ts`) i ta sama idzie do karty
 * zlecenia i do wiersza listy (omówienie 01.10.2026: „VAT ma być osobną
 * kolumną na liście zleceń”). Tu jest tylko odczyt i słowa dla operatora —
 * bez React-a, żeby komórka listy, karta i eksport czytały to tak samo.
 */

export interface VatSuggestion {
  /** '23%' | '19%' | '0%' | 'NP' albo null, gdy brak danych. */
  rate: string | null;
  reason: string;
  /**
   * false = rekomendacja niepewna (od 16.10.2026, pyt. 25: kraj kontrahenta
   * z prefiksu VAT, bo kartoteka nie podaje siedziby); operator się upewnia.
   */
  certain: boolean;
  missing: string[];
}

/** Pole `vatSuggestion` z rekordu; `null`, gdy go nie ma albo ma zły kształt. */
export function vatSuggestionOf(
  record: Record<string, unknown> | undefined,
): VatSuggestion | null {
  const v = record?.vatSuggestion;
  if (!v || typeof v !== 'object') return null;
  const s = v as Partial<VatSuggestion>;
  if (typeof s.reason !== 'string') return null;
  return {
    rate: typeof s.rate === 'string' ? s.rate : null,
    reason: s.reason,
    certain: s.certain === true,
    missing: Array.isArray(s.missing)
      ? s.missing.filter((m): m is string => typeof m === 'string')
      : [],
  };
}

/** Skrót „NP” słowem — w dymku i w eksporcie. */
export const VAT_NOT_SUBJECT = 'NP — nie podlega VAT w Polsce';

/**
 * Stawka w jednej komórce eksportu: „23%”, „NP — nie podlega VAT w Polsce”,
 * z dopiskiem „(do potwierdzenia)”, gdy rekomendacja nie jest pewna.
 * Brak rekomendacji — pusta komórka (to arkusz, nie ekran).
 */
export function vatExportText(vat: VatSuggestion | null): string {
  if (!vat || vat.rate === null) return '';
  const rate = vat.rate === 'NP' ? VAT_NOT_SUBJECT : vat.rate;
  return vat.certain ? rate : `${rate} (do potwierdzenia)`;
}

/**
 * Dymek przy stawce na liście: skąd stawka, czego brakuje i że to tylko
 * rekomendacja. Pełne zdania, bez kodów.
 */
export function vatHint(vat: VatSuggestion): string {
  const reason = vat.reason.charAt(0).toUpperCase() + vat.reason.slice(1);
  return [
    vat.rate === 'NP' ? `${VAT_NOT_SUBJECT}.` : null,
    `${reason}.`,
    vat.missing.length > 0 ? `Brakuje: ${vat.missing.join(', ')}.` : null,
    vat.rate !== null && !vat.certain
      ? 'Rekomendacja nie jest pewna — sprawdź stawkę.'
      : null,
    'To rekomendacja z macierzy VAT — stawkę zatwierdzasz i wpisujesz w TMS.',
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
}
